/* MOURIN — product.js : variant selection, media gallery, lightbox, sticky ATC, quick add */
(() => {
  const strings = (window.theme && window.theme.strings) || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- <product-info> ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-variants]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.form = this.querySelector('form[data-product-form]');
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.picker = this.querySelector('[data-variant-picker]');
      this.gallery = this.querySelector('media-gallery');
      if (this.picker) {
        this.picker.addEventListener('change', () => this.onOptionChange());
        this.markAvailability();
      }
      this.initSticky();
    }

    disconnectedCallback() { if (this.stickyObserver) this.stickyObserver.disconnect(); }

    selectedOptions() {
      return [...this.picker.querySelectorAll('[data-option]')].map((group) => {
        if (group.tagName === 'SELECT') return group.value;
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      this.picker.querySelectorAll('[data-selected-value]').forEach((el, i) => { el.textContent = selected[i] || ''; });
      this.markAvailability();
      this.update(variant);
    }

    /* Mark option values with no available combination given the other current selections */
    markAvailability() {
      const selected = this.selectedOptions();
      this.picker.querySelectorAll('[data-option]').forEach((group, index) => {
        const inputs = group.tagName === 'SELECT' ? [...group.options] : [...group.querySelectorAll('input')];
        inputs.forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const available = this.variants.some((v) => v.available && v.options.every((o, i) => o === candidate[i]));
          if (group.tagName === 'SELECT') {
            const base = input.dataset.label || input.textContent;
            input.dataset.label = base;
            input.textContent = available ? base : `${base} — ${strings.soldOut}`;
          } else {
            input.classList.toggle('is-unavailable', !available);
          }
        });
      });
    }

    update(variant) {
      const buttons = [...this.querySelectorAll('[data-add-button]')];
      if (this.form && this.form.id) document.querySelectorAll(`[data-add-button][form="${this.form.id}"]`).forEach((b) => { if (!buttons.includes(b)) buttons.push(b); });

      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; this.setLabel(b, strings.unavailable); });
        this.querySelectorAll('[data-price]').forEach((el) => el.classList.add('is-unavailable'));
        this.updateInventory(null);
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        this.setLabel(b, variant.available ? strings.addToCart : strings.soldOut);
      });

      const priceHtml = this.priceHtml(variant);
      this.querySelectorAll('[data-price]').forEach((el) => { el.innerHTML = priceHtml; el.classList.remove('is-unavailable'); });
      document.querySelectorAll(`[data-sticky-price="${this.dataset.sectionId}"]`).forEach((el) => { el.innerHTML = priceHtml; });

      this.querySelectorAll('[data-variant-sku]').forEach((el) => { el.textContent = variant.sku || ''; el.hidden = !variant.sku; });
      this.updateInventory(variant);

      if (variant.media && this.gallery) this.gallery.setActiveMedia(variant.media);

      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      const payment = this.querySelector('.shopify-payment-button');
      if (payment) payment.hidden = !variant.available;

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    setLabel(button, text) {
      const label = button.querySelector('[data-label]');
      if (label) label.textContent = text; else button.textContent = text;
    }

    priceHtml(v) {
      let html = `<span class="price__regular">${escapeHtml(v.price)}</span>`;
      if (v.compare) {
        html = `<span class="visually-hidden">${escapeHtml(this.dataset.saleLabel || '')}</span>${html}<s class="price__compare">${escapeHtml(v.compare)}</s>`;
        if (v.discount > 0 && this.dataset.showDiscount === 'true') html += `<span class="price__discount">-${v.discount}%</span>`;
      }
      if (v.unit) html += `<span class="price__unit">${escapeHtml(v.unit)}</span>`;
      return html;
    }

    updateInventory(variant) {
      const box = this.querySelector('[data-inventory]');
      if (!box) return;
      box.className = 'inventory';
      if (!variant) { box.hidden = true; return; }
      box.hidden = false;
      const threshold = parseInt(box.dataset.threshold || '0', 10);
      if (!variant.available) {
        box.classList.add('inventory--out');
        box.textContent = box.dataset.outText;
      } else if (variant.inv !== null && variant.inv > 0 && variant.inv <= threshold && box.dataset.showLow === 'true') {
        box.classList.add('inventory--low');
        box.textContent = (box.dataset.lowText || '').replace('[count]', variant.inv);
      } else {
        box.classList.add('inventory--in');
        box.textContent = box.dataset.inText;
      }
    }

    initSticky() {
      const sticky = document.querySelector(`[data-sticky-atc="${this.dataset.sectionId}"]`);
      const main = this.querySelector('[data-add-button]');
      if (!sticky || !main || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', past);
        sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
        sticky.querySelectorAll('button, a').forEach((el) => { el.tabIndex = past ? 0 : -1; });
      });
      this.stickyObserver.observe(main);
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* ---------- <media-gallery> ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.items = () => [...this.track.querySelectorAll('[data-media-id]')];
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.counter = this.querySelector('[data-counter]');
      this.progress = this.querySelector('.gallery__progress');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];

      this.prev && this.prev.addEventListener('click', () => this.go(this.index() - 1));
      this.next && this.next.addEventListener('click', () => this.go(this.index() + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.setActiveMedia(t.dataset.thumb, true)));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index() - 1); }
      });
      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) this.openLightbox(zoom.closest('[data-media-id]'));
      });
      this.sync();

      if (this.querySelector('model-viewer') && window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([
          { name: 'shopify-xr', version: '1.0', onLoad: () => {} },
          { name: 'model-viewer-ui', version: '1.0', onLoad: (errors) => {
            if (errors || !window.Shopify.ModelViewerUI) return;
            this.querySelectorAll('model-viewer').forEach((el) => { if (!el.dataset.ui) { el.dataset.ui = '1'; new window.Shopify.ModelViewerUI(el); } });
          } }
        ]);
      }
    }

    isStacked() { return this.classList.contains('gallery--stacked') && window.matchMedia('(min-width: 990px)').matches; }

    index() {
      const width = this.track.clientWidth || 1;
      return Math.round(this.track.scrollLeft / width);
    }

    go(i) {
      const items = this.items();
      const target = items[Math.max(0, Math.min(items.length - 1, i))];
      if (target) this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    sync() {
      const items = this.items();
      const i = this.index();
      const total = items.length;
      if (this.counter) this.counter.textContent = `${String(i + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      if (this.prev) this.prev.disabled = i <= 0;
      if (this.next) this.next.disabled = i >= total - 1;
      if (this.progress) this.progress.style.setProperty('--progress', `${((i + 1) / Math.max(total, 1)) * 100}%`);
      const activeId = items[i] && items[i].dataset.mediaId;
      this.thumbs.forEach((t) => t.setAttribute('aria-current', t.dataset.thumb === activeId ? 'true' : 'false'));
      items.forEach((item, idx) => {
        if (idx !== i) item.querySelectorAll('video').forEach((v) => { if (!v.hasAttribute('data-autoplay')) v.pause(); });
      });
    }

    setActiveMedia(mediaId, fromThumb = false) {
      const target = this.items().find((el) => el.dataset.mediaId === String(mediaId));
      if (!target) return;
      if (this.isStacked()) {
        if (!fromThumb) { this.track.prepend(target); }
        return;
      }
      this.go(this.items().indexOf(target));
    }

    openLightbox(item) {
      const dialog = this.querySelector('dialog.lightbox');
      if (!dialog || !window.MourinDialogs) return;
      window.MourinDialogs.open(dialog, item && item.querySelector('[data-zoom]'));
      const slide = item && dialog.querySelector(`[data-lightbox-id="${item.dataset.mediaId}"]`);
      if (slide) requestAnimationFrame(() => { slide.parentElement.scrollLeft = slide.offsetLeft; });
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------- <product-recommendations> ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.querySelector('.card')) {
            this.innerHTML = fresh.innerHTML;
            this.dataset.loaded = 'true';
            window.Mourin && window.Mourin.initReveal(this);
          } else {
            if (!(window.Shopify && window.Shopify.designMode)) this.closest('section').hidden = true;
          }
        } catch (_) { /* ignore */ }
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Quick add ---------- */
  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-quick-add]');
    if (!trigger) return;
    event.preventDefault();
    const modal = document.getElementById('QuickAddModal');
    const content = modal && modal.querySelector('[data-quick-add-content]');
    if (!modal || !content) { window.location.href = trigger.dataset.quickAdd; return; }
    content.innerHTML = '';
    window.MourinDialogs.open(modal, trigger);
    try {
      const url = new URL(trigger.dataset.quickAdd, window.location.origin);
      url.searchParams.set('section_id', 'quick-add');
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const section = doc.querySelector('[data-quick-add-root]');
      if (!section) throw new Error('missing');
      content.innerHTML = '';
      content.appendChild(document.importNode(section, true));
      const first = content.querySelector('input:not([type="hidden"]), select, button[type="submit"]');
      first && first.focus({ preventScroll: true });
    } catch (_) {
      content.innerHTML = `<p class="editor-note" style="margin:3rem 1.5rem">${escapeHtml(strings.quickAddError || '')}</p>`;
    }
  });
})();
